/**
 * /admin/feature-flags - admin UI for feature flags table.
 *
 * Per CHAP-177 (Phase 1N #149). Admin-only. Backed by:
 *   GET    /api/admin/feature-flags             - list rows with IDs
 *   PUT    /api/admin/feature-flags/:key        - upsert (?tenantSlug=...)
 *   DELETE /api/admin/feature-flags/:id         - remove
 *
 * UX: list rows grouped by Global vs per-tenant. Each row has inline
 * JSON value edit + save + delete. New flag form at the top.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiDelete, ApiError, readCsrfToken } from '../../lib/api.js';

interface FlagRow {
  id: string;
  tenantId: string | null;
  tenantSlug: string | null;
  tenantDisplayName: string | null;
  key: string;
  value: unknown;
  updatedAt: string | null;
}

interface FlagsResponse {
  flags: FlagRow[];
  count: number;
}

/**
 * The PUT endpoint uses `key` as a URL param so we hit it with PUT
 * directly (apiPatch reuses PATCH semantics; we wrap fetch for PUT).
 */
async function putFlag(key: string, value: unknown, tenantSlug?: string): Promise<FlagRow> {
  const url = tenantSlug
    ? `/api/admin/feature-flags/${encodeURIComponent(key)}?tenantSlug=${encodeURIComponent(tenantSlug)}`
    : `/api/admin/feature-flags/${encodeURIComponent(key)}`;
  // Reuse the api client's CSRF + credentials by using apiPatch shape against PUT
  const res = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...csrfHeaders(),
    },
    body: JSON.stringify({ value }),
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    throw new ApiError(res.status, body, `${res.url} failed: ${res.status}`);
  }
  return (await res.json()) as FlagRow;
}

function csrfHeaders(): Record<string, string> {
  const token = readCsrfToken(); // CHAP-594: the one shared reader
  return token ? { 'X-CSRF-Token': token } : {};
}

export default function AdminFeatureFlags() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<FlagsResponse, Error>({
    queryKey: ['admin-feature-flags'],
    queryFn: () => apiGet<FlagsResponse>('/api/admin/feature-flags'),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-feature-flags'] });
    qc.invalidateQueries({ queryKey: ['feature-flags'] });
  };

  const upsert = useMutation({
    mutationFn: ({ key, value, tenantSlug }: { key: string; value: unknown; tenantSlug?: string }) =>
      putFlag(key, value, tenantSlug),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/api/admin/feature-flags/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });

  // Group rows: global first, then per-tenant
  const global = data?.flags.filter((f) => f.tenantId === null) ?? [];
  const perTenant = data?.flags.filter((f) => f.tenantId !== null) ?? [];

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">Feature flags</h2>
        <p className="text-neutral text-sm mt-1">
          Global + per-tenant feature flags. Tenant flag wins over global flag
          with the same key. Per CHAP-177.
        </p>
      </header>

      <NewFlagForm
        onSubmit={(key, value, tenantSlug) =>
          upsert.mutate({ key, value, tenantSlug })
        }
        pending={upsert.isPending}
        error={upsert.error}
      />

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
          Loading flags…
        </div>
      )}
      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
          Failed to load: {error.message}
        </div>
      )}

      {data && (
        <>
          <FlagsSection
            title={`Global (${global.length})`}
            description="Apply to every tenant unless overridden by a tenant-scoped flag with the same key."
            flags={global}
            onSave={(key, value, tenantSlug) => upsert.mutate({ key, value, tenantSlug })}
            onDelete={(id) => remove.mutate(id)}
            saveError={upsert.error}
            deleteError={remove.error}
          />
          <FlagsSection
            title={`Per-tenant (${perTenant.length})`}
            description="Tenant-scoped overrides. The tenantSlug shown is the scope this flag applies to."
            flags={perTenant}
            onSave={(key, value, tenantSlug) => upsert.mutate({ key, value, tenantSlug })}
            onDelete={(id) => remove.mutate(id)}
            saveError={upsert.error}
            deleteError={remove.error}
          />
        </>
      )}
    </div>
  );
}

function NewFlagForm({
  onSubmit,
  pending,
  error,
}: {
  onSubmit: (key: string, value: unknown, tenantSlug?: string) => void;
  pending: boolean;
  error: Error | null;
}) {
  const [key, setKey] = useState('');
  const [valueText, setValueText] = useState('true');
  const [tenantSlug, setTenantSlug] = useState('');
  const [localErr, setLocalErr] = useState<string | null>(null);

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <h3 className="text-navy text-sm font-semibold mb-3">New / upsert flag</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
        <Field label="Key (lowercase, [a-z0-9_-])">
          <input
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="voting-v2"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label="Value (JSON literal)">
          <input
            type="text"
            value={valueText}
            onChange={(e) => setValueText(e.target.value)}
            placeholder='true OR {"enabled": true}'
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label="Tenant slug (blank = global)">
          <input
            type="text"
            value={tenantSlug}
            onChange={(e) => setTenantSlug(e.target.value)}
            placeholder="omega-zeta"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
          />
        </Field>
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap min-w-0">
        <button
          type="button"
          disabled={pending || !key}
          onClick={() => {
            try {
              const value = JSON.parse(valueText);
              setLocalErr(null);
              onSubmit(key, value, tenantSlug || undefined);
            } catch {
              setLocalErr('Value must be valid JSON (true / false / 1 / "string" / { ... })');
            }
          }}
          className="bg-navy text-white text-sm font-medium px-4 py-2 rounded hover:bg-navy/90 disabled:opacity-50"
        >
          {pending ? 'Saving…' : 'Save flag'}
        </button>
        {(localErr || error) && (
          <span className="text-xs text-red-700">{localErr ?? error?.message}</span>
        )}
      </div>
    </section>
  );
}

function FlagsSection({
  title,
  description,
  flags,
  onSave,
  onDelete,
  saveError,
  deleteError,
}: {
  title: string;
  description: string;
  flags: FlagRow[];
  onSave: (key: string, value: unknown, tenantSlug?: string) => void;
  onDelete: (id: string) => void;
  saveError: Error | null;
  deleteError: Error | null;
}) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <header className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-navy text-sm font-semibold">{title}</h3>
        <p className="text-neutral text-xs mt-0.5">{description}</p>
      </header>
      <ul className="divide-y divide-gray-100">
        {flags.length === 0 && (
          <li className="p-6 text-center text-neutral text-sm">No flags in this scope.</li>
        )}
        {flags.map((f) => (
          <FlagRowEditor
            key={f.id}
            flag={f}
            onSave={onSave}
            onDelete={onDelete}
            saveError={saveError}
            deleteError={deleteError}
          />
        ))}
      </ul>
    </section>
  );
}

function FlagRowEditor({
  flag,
  onSave,
  onDelete,
  saveError,
  deleteError,
}: {
  flag: FlagRow;
  onSave: (key: string, value: unknown, tenantSlug?: string) => void;
  onDelete: (id: string) => void;
  saveError: Error | null;
  deleteError: Error | null;
}) {
  const [editing, setEditing] = useState(false);
  const [valueText, setValueText] = useState(JSON.stringify(flag.value));
  const [localErr, setLocalErr] = useState<string | null>(null);

  return (
    <li className="p-3 sm:p-4 min-w-0">
      <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="font-mono text-navy text-sm font-semibold">{flag.key}</span>
            {flag.tenantSlug && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-mono">
                {flag.tenantSlug}
              </span>
            )}
            {!flag.tenantSlug && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                global
              </span>
            )}
            {flag.updatedAt && (
              <span className="text-[10px] text-neutral">
                updated {new Date(flag.updatedAt).toLocaleString()}
              </span>
            )}
          </div>
          {!editing && (
            <pre className="mt-1 text-xs font-mono text-neutral bg-gray-50 rounded px-2 py-1 overflow-x-auto">
              {JSON.stringify(flag.value, null, 2)}
            </pre>
          )}
          {editing && (
            <div className="mt-1">
              <textarea
                value={valueText}
                onChange={(e) => setValueText(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono"
              />
              {(localErr || saveError) && (
                <p className="text-xs text-red-700 mt-1">
                  {localErr ?? saveError?.message}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs font-medium px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
            >
              Edit
            </button>
          )}
          {editing && (
            <>
              <button
                type="button"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(valueText);
                    setLocalErr(null);
                    onSave(flag.key, parsed, flag.tenantSlug ?? undefined);
                    setEditing(false);
                  } catch {
                    setLocalErr('Value must be valid JSON');
                  }
                }}
                className="bg-navy text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-navy/90"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setValueText(JSON.stringify(flag.value));
                  setLocalErr(null);
                }}
                className="text-xs font-medium px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Delete flag "${flag.key}"${flag.tenantSlug ? ` (tenant ${flag.tenantSlug})` : ' (global)'}?`)) {
                onDelete(flag.id);
              }
            }}
            className="text-xs font-medium px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>
      {deleteError && (
        <p className="text-xs text-red-700 mt-1">Delete failed: {deleteError.message}</p>
      )}
    </li>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="block text-xs font-medium text-navy mb-1">{label}</span>
      {children}
    </label>
  );
}
